/*
 * What a booking costs, and how long it takes, once a tier and some add-ons are chosen.
 *
 * ⚠️ ONE IMPLEMENTATION, ON PURPOSE, and it lives here for exactly the reason contrast.ts
 * does: the API is the authority on the figure, the booking page has to show the same figure
 * live as the customer ticks boxes, and @smup/booking-ui is a React package that must not be
 * dragged into the API's container image. This package is pure and the API already depends
 * on it.
 *
 * The stakes are higher than they look. The page shows a total, the customer agrees to it,
 * the server stores it, and the confirmation email repeats it. Two copies of this arithmetic
 * would eventually disagree, and the failure is a customer who was quoted $130 reading $150
 * in her email — which is indistinguishable from being cheated.
 *
 * Everything here is integer cents. No floats: 10% off $13,000 must not be 11699.999999999.
 */

/** Flat, or per-minute. See the ⚠️ on `estimate`. */
export type AddonUnit = 'flat' | 'minute';

export interface PricedTier {
  label: string;
  priceCents: number;
  /** Top of a range. NULL is "this exact figure", not "unbounded". */
  priceMaxCents: number | null;
  /** NULL inherits the service's duration. */
  durationMinutes: number | null;
}

export interface PricedAddon {
  name: string;
  priceCents: number;
  priceMaxCents: number | null;
  priceUnit: AddonUnit;
  durationMinutes: number;
}

export interface PricedService {
  durationMinutes: number;
  pricingMode: 'fixed' | 'quote';
  priceCents: number | null;
  priceMaxCents: number | null;
  priceIsEstimate: boolean;
  tiered: boolean;
}

export interface EstimateInput {
  service: PricedService;
  /** The tier the customer picked. Required when `service.tiered`. */
  tier?: PricedTier | null;
  addons?: PricedAddon[];
  /** 0–100. What the chosen repeat interval is worth off. */
  discountPercent?: number;
}

export interface Estimate {
  /**
   * The floor, in cents, or null when nothing can be priced remotely (quote-mode). Null is
   * "no figure", never "free" — the caller must not render it as $0.
   */
  minCents: number | null;
  /**
   * The ceiling. Null alongside a non-null `minCents` means the page says "and up" rather
   * than showing a closed range — a different statement, and the one the operator needs to
   * know the customer saw.
   */
  maxCents: number | null;
  /** Whether every figure above is a floor rather than a promise. */
  isEstimate: boolean;
  /** How long to reserve, service or tier plus flat add-ons. Drives the slot grid. */
  durationMinutes: number;
  /** Cents taken off by the recurring discount, for the line that shows the saving. */
  discountCents: number;
  /**
   * Add-ons that could not be priced up front — the per-minute ones. They are EXCLUDED from
   * the figures above and listed separately so the page can say so out loud.
   */
  unpricedAddons: PricedAddon[];
}

/** Whole cents, half away from zero. A discount must never leave a fractional cent. */
const applyPercent = (cents: number, percent: number): number =>
  Math.round((cents * (100 - percent)) / 100);

/**
 * The figure the customer is shown, and the figure the server stores.
 *
 * ⚠️ A 'minute' ADD-ON CONTRIBUTES NOTHING TO THE TOTAL, and that is deliberate rather than
 * unfinished. Dematting at $1–2/min has no total until the dog is on the table. Inventing a
 * duration to multiply by would either inflate a quote she never agreed to or — worse —
 * understate one and leave the operator arguing about it in a driveway. It comes back in
 * `unpricedAddons` so the page can show the rate and say the work is added only if needed.
 */
export function estimate(input: EstimateInput): Estimate {
  const { service, tier = null, addons = [], discountPercent = 0 } = input;

  const flat = addons.filter((a) => a.priceUnit === 'flat');
  const unpricedAddons = addons.filter((a) => a.priceUnit !== 'flat');

  /*
   * Duration first, because it is the one number that exists even when the price does not.
   * A quote-mode visit still consumes the day, which is why 0006 made duration required on
   * every service (see the note there).
   */
  const durationMinutes =
    (tier?.durationMinutes ?? service.durationMinutes) +
    flat.reduce((sum, a) => sum + a.durationMinutes, 0);

  // Quote-mode publishes no number at all, so there is nothing to total. Add-ons ticked
  // alongside it are still recorded and still lengthen the visit — she just has not said
  // what any of it costs yet.
  if (service.pricingMode === 'quote') {
    return {
      minCents: null,
      maxCents: null,
      isEstimate: service.priceIsEstimate,
      durationMinutes,
      discountCents: 0,
      unpricedAddons: addons,
    };
  }

  const baseMin = tier ? tier.priceCents : service.priceCents;
  // A tiered service holds no price of its own, so a missing tier leaves nothing to add up.
  // The API refuses this upstream; returning "no figure" rather than 0 means a bug here
  // surfaces as a blank price instead of a free groom.
  if (baseMin == null) {
    return {
      minCents: null,
      maxCents: null,
      isEstimate: service.priceIsEstimate,
      durationMinutes,
      discountCents: 0,
      unpricedAddons: addons,
    };
  }

  const baseMax = tier ? tier.priceMaxCents : service.priceMaxCents;

  const addonsMin = flat.reduce((sum, a) => sum + a.priceCents, 0);
  /*
   * An add-on with no ceiling contributes its own floor to the ceiling, not nothing —
   * otherwise ticking a $20 nail trim onto a $130–$170 groom would show $130–$170 and the
   * top of the range would silently stop including it.
   */
  const addonsMax = flat.reduce((sum, a) => sum + (a.priceMaxCents ?? a.priceCents), 0);

  const grossMin = baseMin + addonsMin;
  /*
   * The ceiling exists only if something actually has one. A closed range and an open-ended
   * "$190+" are different promises, and collapsing them here would turn every open-ended
   * price into a cap the operator never agreed to.
   */
  const hasCeiling = baseMax != null || flat.some((a) => a.priceMaxCents != null);
  const grossMax = hasCeiling ? (baseMax ?? baseMin) + addonsMax : null;

  const pct = Math.max(0, Math.min(100, Math.round(discountPercent)));
  const minCents = applyPercent(grossMin, pct);
  const maxCents = grossMax == null ? null : applyPercent(grossMax, pct);

  return {
    minCents,
    maxCents,
    /*
     * A range is an estimate whether or not anyone ticked the box. If the customer is shown
     * two numbers, the page cannot also claim the first one is what she pays — and a service
     * whose add-on carries a range inherits that even when the service itself is fixed.
     */
    isEstimate: service.priceIsEstimate || maxCents != null,
    durationMinutes,
    discountCents: grossMin - minCents,
    unpricedAddons,
  };
}

/**
 * What a whole VISIT costs — one arrival, one or more dogs (#134).
 *
 * ⚠️ THE DISCOUNTS COMPOSE ON THE VISIT TOTAL, NOT PER BOOKING, and that is the decision this
 * function exists to encode (Mike, 2026-08-14). Storing a shared percentage per booking would
 * need a rule for splitting it across rows, and any split rounds — so the rows would
 * eventually sum to a cent away from the figure the customer read on screen.
 *
 * ⚠️ THE TWO DISCOUNTS ARE INDEPENDENT AND BOTH CAN APPLY:
 *
 *  - `recurringPercent` is a property of THE ARRANGEMENT. It applies to every visit on the
 *    cycle, whether that visit brings one dog or two.
 *  - `multiPetPercent` is a property of THIS VISIT'S COMPOSITION, recalculated each time. A
 *    customer on a 4-week cycle who alternates one dog and two sees the repeat percentage
 *    every visit and this one only on the two-dog ones.
 *
 * ⚠️ THEY ARE SUMMED, NOT COMPOUNDED. 10% + 10% is 20% off, not 19%. Compounding is
 * defensible arithmetic and indefensible on a price list: an operator who advertises "10% off
 * repeats and 10% off multi-dog" and hands over a bill showing 19% is explaining her own
 * discount policy in a driveway. Capped at 100 so a generous pair cannot invert the total.
 *
 * ⚠️ THE TRAVEL UPCHARGE IS APPLIED AFTER THE DISCOUNTS, once per visit — two dogs in one
 * extended-area driveway is one drive (#135). Nothing passes it yet.
 */
export interface VisitEstimateInput {
  /** One per dog, already priced by `estimate()`. Their own snapshots are unaffected. */
  lines: Pick<Estimate, 'minCents' | 'maxCents' | 'isEstimate' | 'durationMinutes'>[];
  /** 0–100. What the chosen repeat interval is worth off, or 0. */
  recurringPercent?: number;
  /** 0–100, applied only when the visit holds more than one line. NULL/0 = not offered. */
  multiPetPercent?: number | null;
  /** 0–100. #135's extended-area charge. Applied last, to the discounted total. */
  travelUpchargePercent?: number | null;
}

export interface VisitEstimate {
  /** The sum of the lines, before anything is taken off or added on. NULL = nothing priceable. */
  subtotalCents: number | null;
  subtotalMaxCents: number | null;
  /** What was actually applied, for the visit row and for the lines the page shows. */
  recurringPercent: number;
  multiPetPercent: number;
  travelUpchargePercent: number;
  /** ⚠️ THE FIGURE THE CUSTOMER READS, after both discounts and any upcharge. */
  totalCents: number | null;
  totalMaxCents: number | null;
  /** Every line's duration, summed. What the slot grid must reserve. */
  durationMinutes: number;
  isEstimate: boolean;
}

const clampPercent = (p: number | null | undefined): number =>
  Math.max(0, Math.min(100, Math.round(p ?? 0)));

export function estimateVisit(input: VisitEstimateInput): VisitEstimate {
  const { lines } = input;

  /*
   * ⚠️ DURATION IS THE SUM, AND IT EXISTS EVEN WHEN THE PRICE DOES NOT. Two Full Grooms is
   * four hours in the driveway, not two — this is the number the slot grid must reserve, and
   * getting it wrong double-books her on her own street.
   */
  const durationMinutes = lines.reduce((sum, l) => sum + l.durationMinutes, 0);

  const recurringPercent = clampPercent(input.recurringPercent);
  // ⚠️ ONLY WHEN THERE IS ACTUALLY MORE THAN ONE ANIMAL. A one-dog visit must never receive
  // a multi-dog rate, however the caller configured it.
  const multiPetPercent = lines.length > 1 ? clampPercent(input.multiPetPercent) : 0;
  const travelUpchargePercent = clampPercent(input.travelUpchargePercent);

  /*
   * ⚠️ ONE UNPRICEABLE LINE MAKES THE WHOLE VISIT UNPRICEABLE. A quote-mode dog alongside a
   * fixed-price one has no figure, so the visit has no total — showing the sum of only the
   * lines that happen to have prices would quote a number that covers some of the work and
   * read as though it covered all of it. NULL is "no figure", never "free".
   */
  const priceable = lines.every((l) => l.minCents != null);
  if (!priceable || lines.length === 0) {
    return {
      subtotalCents: null,
      subtotalMaxCents: null,
      recurringPercent,
      multiPetPercent,
      travelUpchargePercent,
      totalCents: null,
      totalMaxCents: null,
      durationMinutes,
      isEstimate: lines.some((l) => l.isEstimate),
    };
  }

  const subtotalCents = lines.reduce((sum, l) => sum + l.minCents!, 0);
  /*
   * A line with no ceiling contributes its own floor to the ceiling, not nothing — the same
   * rule `estimate()` applies to add-ons, for the same reason. The visit has a ceiling only
   * if some line actually has one; collapsing an open-ended "$190+" into a cap would promise
   * something the operator never agreed to.
   */
  const anyCeiling = lines.some((l) => l.maxCents != null);
  const subtotalMaxCents = anyCeiling
    ? lines.reduce((sum, l) => sum + (l.maxCents ?? l.minCents!), 0)
    : null;

  // Summed, not compounded — see the ⚠️ above. Capped so the total cannot go negative.
  const discountPercent = Math.min(100, recurringPercent + multiPetPercent);

  const applyAll = (gross: number): number => {
    const discounted = applyPercent(gross, discountPercent);
    // The upcharge lands on the discounted figure, and rounds once, in cents.
    return discounted + Math.round((discounted * travelUpchargePercent) / 100);
  };

  return {
    subtotalCents,
    subtotalMaxCents,
    recurringPercent,
    multiPetPercent,
    travelUpchargePercent,
    totalCents: applyAll(subtotalCents),
    totalMaxCents: subtotalMaxCents == null ? null : applyAll(subtotalMaxCents),
    durationMinutes,
    // A range is an estimate whether or not anyone ticked the box — same rule as a line.
    isEstimate: lines.some((l) => l.isEstimate) || subtotalMaxCents != null,
  };
}

/** Cents to a display string. "$45" reads better than "$45.00" on a price list. */
export const formatCents = (cents: number): string =>
  cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

/**
 * The whole price as one string — "$130", "$130–$170", "From $130", "$190+".
 *
 * ⚠️ SHARED BY THE CARD, THE CONFIRMATION SCREEN AND THE EMAIL. Three places rendering a
 * price three ways is how a customer ends up reading "$130" on the page and "From $130" in
 * her inbox and wondering which one she agreed to.
 */
export function formatEstimate(e: Pick<Estimate, 'minCents' | 'maxCents' | 'isEstimate'>): string {
  if (e.minCents == null) return 'Quote';
  const min = formatCents(e.minCents);
  if (e.maxCents != null && e.maxCents !== e.minCents) return `${min}–${formatCents(e.maxCents)}`;
  // Open-ended: she has named a floor and nothing above it.
  if (e.isEstimate) return `${min}+`;
  return min;
}

/**
 * Split a visit's agreed total back across its lines.
 *
 * ⚠️ THIS EXISTS BECAUSE THE DISCOUNT LIVES ON THE VISIT AND THE BILL IS RAISED PER LINE
 * (#168). A customer is quoted `visits.total_cents` — after the repeat discount, the multi-dog
 * discount and any travel upcharge — but completion mints a job and an invoice for ONE animal,
 * and the only figure on that animal is its own undiscounted line. So the discount was quoted
 * and never charged: she billed the sum of the lines, which is more than the page said.
 *
 * ⚠️ THE SHARES SUM EXACTLY TO THE TOTAL, WHICH IS THE WHOLE DIFFICULTY. Rounding each line's
 * proportion independently loses or gains a cent or two, and the customer then gets bills that
 * do not add up to the number she was given. Apportioning by PREFIX — each share is the
 * difference between two rounded running totals — makes the error cancel at every step, so the
 * parts always reconstruct the whole.
 *
 * ⚠️ AND EACH SHARE DEPENDS ONLY ON THE ORDERED LINES, never on which animal is being completed
 * or in what order. Completion happens one dog at a time, minutes or days apart; a scheme where
 * "the last one absorbs the remainder" would need to know which one that is, and it cannot.
 *
 * @param lines each animal's own undiscounted figure, in the visit's order.
 * @param totalCents what the customer actually agreed for the whole visit.
 * @returns one share per line, summing exactly to `totalCents`. Empty when nothing is
 *   apportionable — no lines, no total, or a subtotal of zero, all of which mean the caller
 *   should fall back to the line's own figure rather than invent one.
 */
export function apportionVisitTotal(lines: number[], totalCents: number | null): number[] {
  if (totalCents == null || lines.length === 0) return [];
  const subtotal = lines.reduce((n, c) => n + c, 0);
  // A visit of free work, or of nothing priceable. There is no proportion to divide by.
  if (subtotal <= 0) return [];

  const shares: number[] = [];
  let running = 0;
  let allocated = 0;
  for (const line of lines) {
    running += line;
    // The rounded running total, minus everything handed out so far.
    const upTo = Math.round((totalCents * running) / subtotal);
    shares.push(upTo - allocated);
    allocated = upTo;
  }
  return shares;
}
